/*
 * OIDC callback handler for AD login via MAS/Dex
 */

import { type FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createClient } from "matrix-js-sdk";

import { useClient } from "../ClientContext";
import { Config } from "../config/Config";
import { initClient } from "../utils/matrix";

export const OidcCallbackPage: FC = () => {
  const { setClient } = useClient();
  const navigate = useNavigate();
  const [error, setError] = useState<string>();

  useEffect(() => {
    const run = async (): Promise<void> => {
      const params = new URLSearchParams(window.location.search);
      const code = params.get("code");
      const state = params.get("state");

      const stored = sessionStorage.getItem("oidc_pkce");
      if (!stored) { setError("Нет PKCE данных в сессии"); return; }
      const { code_verifier, state: storedState } = JSON.parse(stored);
      sessionStorage.removeItem("oidc_pkce");

      if (state !== storedState) { setError("Некорректный state"); return; }
      if (!code) { setError("Нет authorization code"); return; }

      // Читаем OIDC конфиг напрямую из config.json
      let oidcConfig: { client_id: string; issuer: string } | undefined;
      try {
        const resp = await fetch("/config.json");
        const cfg = await resp.json();
        oidcConfig = cfg.oidc;
      } catch { setError("Не удалось загрузить config.json"); return; }

      if (!oidcConfig) { setError("OIDC не настроен"); return; }

      const issuer = oidcConfig.issuer.endsWith("/") ? oidcConfig.issuer : oidcConfig.issuer + "/";
      const tokenEndpoint = issuer + "oauth2/token";
      const redirectUri = window.location.origin + "/oidc-callback";

      // Обмен кода на токен
      const tokenResp = await fetch(tokenEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
          grant_type: "authorization_code",
          code,
          client_id: oidcConfig.client_id,
          redirect_uri: redirectUri,
          code_verifier,
        }),
      });

      if (!tokenResp.ok) {
        const txt = await tokenResp.text();
        setError("Ошибка обмена токена: " + txt);
        return;
      }

      const tokenData = await tokenResp.json();
      const accessToken: string = tokenData.access_token;

      // Получаем user_id и device_id
      const homeserver = Config.defaultHomeserverUrl() ?? "";
      const whoami = await fetch(homeserver + "/_matrix/client/v3/account/whoami", {
        headers: { Authorization: "Bearer " + accessToken },
      });
      if (!whoami.ok) { setError("Ошибка whoami"); return; }
      const { user_id, device_id } = await whoami.json();

      const session = { user_id, device_id, access_token: accessToken, passwordlessUser: false };

      const client = await initClient(
        { baseUrl: homeserver, accessToken, userId: user_id, deviceId: device_id },
        false,
      );

      setClient?.(client, session);
      await navigate("/");
    };

    run().catch((e) => setError(String(e)));
  }, [navigate, setClient]);

  if (error) return <div style={{ color: "red", padding: 20 }}>Ошибка входа: {error}</div>;
  return <div style={{ padding: 20 }}>Выполняется вход через AD...</div>;
};
